import { useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  canWriteScope,
  hasWritableRole,
  resolveHiddenFeatures,
  type DivisionId,
  type UserRole,
  type WriteScope,
} from '@atlas/shared';
import { api } from '@/services/api';
import { isFeatureHidden } from '@/config/featureIds';
import { useAuth } from '@/contexts/AuthContext';
import { usePreviewDivision, usePreviewRole } from '@/lib/previewRole';

const QUERY_KEY = ['feature-visibility'] as const;

export function useHiddenFeatures() {
  const { data } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => api.featureVisibility.getHidden(),
    staleTime: 5 * 60 * 1000,
  });
  // "View as": while an admin previews a role and/or a division, resolve the set
  // for THAT scope from the raw configuration instead of using the server's
  // answer for the caller. The by-role endpoint is admin-only, so a non-admin who
  // forces the flag gets no data and falls straight back to their own view.
  //
  // Previewing only a division leaves the roles half as the admin's own, which is
  // the honest reading: a division is not a role, and pretending it strips role
  // hides too would show a view no real user has. Roles are a LIST for the same
  // reason: hides resolve all-must-hide, so previewing one role of a two-role
  // user shows less than that user gets.
  const previewRoles = usePreviewRole();
  const previewDivision = usePreviewDivision();
  const previewing = !!previewRoles || !!previewDivision;
  const { data: config } = useFeatureVisibilityByRole({ enabled: previewing });
  const hiddenFeatures = useMemo(
    () =>
      new Set(
        previewing && config
          ? resolveHiddenFeatures(
              config.hiddenFeatures,
              config.hiddenFeaturesByRole,
              previewRoles ?? [],
              config.hiddenFeaturesByDivision,
              previewDivision ? [previewDivision] : undefined,
            )
          : (data?.hiddenFeatures ?? []),
      ),
    [data?.hiddenFeatures, previewing, previewRoles, previewDivision, config],
  );
  const isHidden = useCallback(
    (id: string) => isFeatureHidden(id, hiddenFeatures),
    [hiddenFeatures],
  );
  return { hiddenFeatures, isHidden };
}

/**
 * Whether the current user may change things in `scope` (Admin → Feature
 * Visibility → Editing). Use it to hide an edit affordance rather than letting
 * the user press Save and collect a 403.
 *
 * Display only, like every other hook here — the server re-checks on every
 * mutation via `requireWrite`, which is the actual boundary. While an admin is
 * previewing a role this answers for THAT role, so the preview shows the edit
 * buttons that role would have.
 */
export function useCanWrite(scope: WriteScope): boolean {
  const { hiddenFeatures } = useHiddenFeatures();
  return canWriteScope(scope, [...hiddenFeatures]);
}

/**
 * Can the viewer edit a product? The client mirror of the server's two gates on
 * product mutations: a writable role (`requireInternalWriter`/`requireRole`) AND
 * Products editing not switched off (`requireWrite('products')`). Display only.
 */
export function useCanEditProducts(): boolean {
  const { user } = useAuth();
  return useCanWrite('products') && hasWritableRole(user?.roles ?? []);
}

export function useSetHiddenFeatures() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (hiddenFeatures: string[]) => api.featureVisibility.setHidden(hiddenFeatures),
    onSuccess: (data) => {
      queryClient.setQueryData(QUERY_KEY, data);
      queryClient.invalidateQueries({ queryKey: ['feature-visibility', 'by-role'] });
    },
  });
}

// ─── Per-role configuration (admin only, issue #540) ────────────────────────
// Separate query from the one above on purpose: that one is the effective set
// for the current user, this one is the raw configuration being edited. Sharing
// a cache key would make the admin page show its own resolved view as the
// stored state and write it back on the next toggle.

const BY_ROLE_KEY = ['feature-visibility', 'by-role'] as const;

export function useFeatureVisibilityByRole({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: BY_ROLE_KEY,
    queryFn: () => api.featureVisibility.getByRole(),
    staleTime: 5 * 60 * 1000,
    enabled,
  });
}

export function useSetHiddenFeaturesForDivision() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      division,
      hiddenFeatures,
    }: {
      division: DivisionId;
      hiddenFeatures: string[];
    }) => api.featureVisibility.setForDivision(division, hiddenFeatures),
    onSuccess: (data) => {
      queryClient.setQueryData(BY_ROLE_KEY, data);
      queryClient.invalidateQueries({ queryKey: QUERY_KEY, exact: true });
    },
  });
}

/** Replace the divisions one division may also see. Same cache handling as the brands. */
export function useSetDivisionAlsoSees() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ division, divisions }: { division: DivisionId; divisions: DivisionId[] }) =>
      api.featureVisibility.setDivisionAlsoSees(division, divisions),
    onSuccess: (data) => {
      queryClient.setQueryData(BY_ROLE_KEY, data);
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

/** Replace the brands one division may see. Same cache handling as the overlays. */
export function useSetDivisionBrands() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ division, brands }: { division: DivisionId; brands: string[] }) =>
      api.featureVisibility.setDivisionBrands(division, brands),
    onSuccess: (data) => {
      queryClient.setQueryData(BY_ROLE_KEY, data);
      // Product lists are brand-scoped server-side, so the admin's own results
      // can change the moment this saves.
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

export function useSetHiddenFeaturesForRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ role, hiddenFeatures }: { role: UserRole; hiddenFeatures: string[] }) =>
      api.featureVisibility.setForRole(role, hiddenFeatures),
    onSuccess: (data) => {
      queryClient.setQueryData(BY_ROLE_KEY, data);
      // The admin may hold the role they just edited, so their own effective set
      // can have changed — refetch it rather than leaving stale nav on screen.
      // `exact` so this does not also blow away the by-role data just written:
      // QUERY_KEY is a prefix of BY_ROLE_KEY.
      queryClient.invalidateQueries({ queryKey: QUERY_KEY, exact: true });
    },
  });
}
